/**
 * @file ImpersonationBanner.tsx
 * @description Полоса «вход под сотрудником» (phase06; phase19.31 — SC24).
 *
 * Закреплена в каркасе (`AppShell banner`) над шапкой, на всю ширину, и не
 * уезжает при прокрутке. Показывает сотрудника, его роль, магазин и режим
 * («только просмотр» / «действия от его имени») — всё из `/auth/me`. Сами
 * режимы, запреты и журнал — impersonation.md, здесь только показ.
 *
 * Главное в полосе — ИМЯ сотрудника, оно не обрезается (дефект приёмки
 * 2026-10-02: на 360 «Под учётной записью А…», на 768 «Владел…»):
 * - телефон: «Вы — <имя>» и ниже (свёрнуто) магазин и причина; роль и
 *   режим — по нажатию; кнопка «В платформу» (не «Выйти»: это не выход из
 *   аккаунта, QA D-B11);
 * - от 768: «Вы работаете под учётной записью <имя> · роль · магазин ·
 *   режим» — текст переносится на вторую строку вместо многоточия, кнопка
 *   «Вернуться в платформу».
 * phase19.28 (PL17): если вход начат с причиной, она стоит последней
 * («· причина: …») — на телефоне в окне по нажатию.
 * Высота полосы растёт вместе с текстом — каркас меряет её и сдвигает
 * шапку и содержимое (`--shell-top`, `AppShell`).
 */

import { UserCog } from "lucide-react";
import { useState } from "react";

import { useAuth } from "../auth/hooks";
import type { ImpersonationMeInfo } from "../auth/types";
import { ApiError } from "../lib/api-client";
import { stopImpersonation } from "../lib/api-impersonation";
import { useNotify } from "./ui/NotifyProvider";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/** Режим входа → слова для человека. Реестр, а не условие. */
const MODE_LABEL: Record<ImpersonationMeInfo["mode"], string> = {
  VIEW: "только просмотр",
  ACT: "действия от его имени",
};

export function ImpersonationBanner() {
  const { me } = useAuth();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const imp = me?.impersonation;
  if (!me || !imp) return null;

  const name = me.user.displayName ?? me.user.email;
  const role = me.activeContext?.role.displayName;
  const shop = imp.shop?.displayName ?? me.activeContext?.shop?.displayName;
  // Порядок «имя · роль · магазин · режим»: роль — сразу за именем.
  const reason = imp.reason?.trim() ? `причина: ${imp.reason.trim()}` : null;
  const details = [role, shop ? `магазин «${shop}»` : null, MODE_LABEL[imp.mode], reason].filter(Boolean).join(" · ");
  // Телефон: магазин и причина — второй строкой; роль — в окне по нажатию.
  const mobileLine = [shop ? `магазин «${shop}»` : null, reason].filter(Boolean).join(" · ") || role || "";
  const extra = [shop ? `магазин «${shop}»` : null, MODE_LABEL[imp.mode], reason].filter(Boolean).join(" · ");

  async function onStop() {
    setSubmitting(true);
    try {
      await stopImpersonation();
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось завершить вход под сотрудником");
      setSubmitting(false);
    }
  }

  return (
    <div
      role="status"
      data-testid="impersonation-banner"
      className="flex min-h-12 items-center gap-3 bg-[var(--warning-soft)] px-4 py-1.5 text-on-surface [box-shadow:inset_0_-1px_0_var(--warning-line)]"
    >
      <UserCog className="size-5 shrink-0 text-warning" aria-hidden />
      {/* Телефон: имя, второй строкой (свёрнуто) — магазин и причина (QA D-B11); всё — по нажатию. */}
      <Popover>
        <PopoverTrigger
          render={(props) => (
            <button
              {...props}
              type="button"
              className="flex min-h-12 min-w-0 flex-1 flex-col justify-center text-left md:hidden"
            >
              <span className="type-body break-words">
                Вы — <strong>{name}</strong>
              </span>
              {mobileLine ? <span className="type-caption line-clamp-3 break-words text-on-surface-variant">{mobileLine}</span> : null}
            </button>
          )}
        />
        <PopoverContent align="start" className="max-w-[calc(100vw-2rem)] text-sm md:hidden">
          <p className="font-medium">{name}</p>
          <p className="text-on-surface-variant">{role ? `${role}${extra ? ` · ${extra}` : ""}` : extra}</p>
        </PopoverContent>
      </Popover>
      <p className="type-body hidden min-w-0 flex-1 break-words md:block" title={`${name} · ${details}`}>
        Вы работаете под учётной записью <strong>{name}</strong>
        {details ? ` · ${details}` : null}
      </p>
      <Button size="sm" variant="outline" onClick={() => void onStop()} disabled={submitting} className="shrink-0">
        <span className="md:hidden">{submitting ? "Выходим…" : "В платформу"}</span>
        <span className="hidden md:inline">{submitting ? "Выходим…" : "Вернуться в платформу"}</span>
      </Button>
    </div>
  );
}
